const MOVEMENT_LABELS = {
  MANUAL_ADJUSTMENT: "Ajuste manual",
  SALE: "Venta",
  SYNC_ADJUSTMENT: "Sincronización de stock",
  INITIAL_IMPORT: "Importación inicial",
  ORDER_CANCEL: "Cancelación de orden",
};

const MOVEMENT_VARIANTS = {
  MANUAL_ADJUSTMENT: "manual",
  SALE: "sale",
  SYNC_ADJUSTMENT: "sync",
  INITIAL_IMPORT: "import",
  ORDER_CANCEL: "cancellation",
};

const CHANNEL_LABELS = {
  MERCADO_LIBRE: "Mercado Libre",
  TIENDA_NUBE: "Tienda Nube",
  MERCURYCOM: "MercuryCom",
  SHOPIFY: "Shopify",
};

const SOURCE_LABELS = {
  USER: "Usuario",
  WEBHOOK: "Webhook",
  SYNC: "Sistema",
  SYSTEM: "Sistema",
};

const humanizeTechnicalValue = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  return normalized
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
};

export const getMovementLabel = (type) =>
  MOVEMENT_LABELS[type] || humanizeTechnicalValue(type) || "Movimiento";

export const getMovementVariant = (type) =>
  MOVEMENT_VARIANTS[type] || "neutral";

export const formatChannelName = (channel) =>
  CHANNEL_LABELS[channel] ||
  humanizeTechnicalValue(channel) ||
  "Canal no informado";

export const formatSourceName = (source) =>
  SOURCE_LABELS[source] ||
  humanizeTechnicalValue(source) ||
  "Origen no informado";

export const formatMovementDateTime = (value) => {
  if (!value) return "Fecha no informada";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const parts = new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const part = (type) =>
    parts.find((entry) => entry.type === type)?.value || "";
  return `${part("day")}/${part("month")}/${part("year")} · ${part("hour")}:${part("minute")}`;
};

export const formatStockChange = (before, after, delta) => {
  const numericBefore = Number(before || 0);
  const numericAfter = Number(after || 0);
  const numericDelta = Number(delta || 0);
  return {
    before: numericBefore,
    after: numericAfter,
    transition: `${numericBefore} → ${numericAfter}`,
    deltaLabel:
      numericDelta > 0
        ? `+${numericDelta}`
        : numericDelta < 0
          ? String(numericDelta)
          : "",
    deltaTone:
      numericDelta > 0 ? "positive" : numericDelta < 0 ? "negative" : "neutral",
  };
};

export const getMovementSubtitle = (movement) => {
  if (movement.presentation_type === "KIT_SALE") {
    const count = Number(movement.component_count || 0);
    return `${count} ${count === 1 ? "componente actualizado" : "componentes actualizados"}`;
  }
  if (movement.movement_type === "MANUAL_ADJUSTMENT") {
    // Never fall back to actor_id: for manual adjustments it is an internal UUID.
    const actorLabel = movement.actor_display_label || movement.actor_label || "";
    return actorLabel ? `Hecho por: ${actorLabel}` : "Hecho por usuario";
  }
  if (movement.movement_type === "SALE") return "";
  if (movement.movement_type === "SYNC_ADJUSTMENT") {
    return movement.source_type === "USER"
      ? "Iniciada por usuario"
      : "Actualización automática";
  }
  if (movement.movement_type === "INITIAL_IMPORT") return "Siembra inicial";
  if (movement.movement_type === "ORDER_CANCEL") return "Stock restaurado";
  return formatSourceName(movement.source_type);
};

export const getMovementContext = (movement) => {
  const channel = formatChannelName(movement.source_channel);
  if (
    movement.movement_type === "SALE" ||
    movement.movement_type === "ORDER_CANCEL"
  ) {
    return {
      primary: channel,
      secondary: movement.external_order_id
        ? `Orden #${movement.external_order_id}`
        : "",
    };
  }
  if (movement.movement_type === "SYNC_ADJUSTMENT") {
    return {
      primary: movement.source_channel
        ? `Desde ${channel}`
        : "Actualización interna",
      secondary: "",
    };
  }
  if (movement.movement_type === "INITIAL_IMPORT") {
    return {
      primary: movement.source_channel
        ? `Desde ${channel}`
        : "Importación inicial",
      secondary: "",
    };
  }
  return {
    primary: channel,
    secondary:
      movement.actor_display_label ||
      movement.actor_label ||
      movement.actor_id ||
      "",
  };
};

const SALE_ERROR_REASON_LABELS = {
  application_failed: "Falló el procesamiento de la orden",
  insufficient_stock: "Stock insuficiente",
  listing_not_imported: "La publicación no está importada",
  listing_not_found: "No se encontró la publicación",
  warehouse_not_found: "No se encontró un depósito para descontar el stock",
  stock_record_missing: "No se encontró el registro de stock",
};

const SALE_NO_CHANGE_REASON_LABELS = {
  pilot_item_disabled: "El producto está excluido de la sincronización",
  user_sync_disabled: "La sincronización de stock está desactivada",
};

const formatErrorCount = (failed) =>
  `${failed} ${Number(failed) === 1 ? "error" : "errores"}`;

const pluralizePublications = (count) =>
  `${count} ${count === 1 ? "publicación" : "publicaciones"}`;

const isOrderFanOut = (reason) =>
  reason === "order_fan_out" ||
  reason === "order_cancellation_fan_out" ||
  reason === "order_fan_out_auto_imported_origin_sync";

const getMarketplaceOrderAppliedCount = (movement) => {
  const metadataCount =
    movement?.metadata?.fanOutPresentation?.marketplaceOrderAppliedCount;
  return Number(
    metadataCount ?? movement?.fan_out_marketplace_order_applied_count ?? 0,
  );
};

export const getFanOutOutcome = (movement) => {
  const reason = movement?.metadata?.reason ?? movement?.metadata_reason;
  if (!isOrderFanOut(reason)) return null;

  const metadata = movement?.metadata || {};
  const updated = Array.isArray(metadata.targetsUpdated)
    ? metadata.targetsUpdated.length
    : Number(movement?.fan_out_updated_count || 0);
  const failed = Array.isArray(metadata.targetsFailed)
    ? metadata.targetsFailed.length
    : Number(movement?.fan_out_failed_count || 0);
  const skipped = Array.isArray(metadata.targetsSkipped)
    ? metadata.targetsSkipped.length
    : Number(movement?.fan_out_skipped_count || 0);
  const internalOnly = Array.isArray(metadata.targetsInternalOnly)
    ? metadata.targetsInternalOnly.length
    : Number(movement?.fan_out_internal_only_count || 0);
  const marketplaceOrderApplied = getMarketplaceOrderAppliedCount(movement);

  return {
    updated,
    failed,
    skipped,
    internalOnly,
    marketplaceOrderApplied,
    completedCount: updated + marketplaceOrderApplied + internalOnly,
    total: updated + failed + skipped + internalOnly + marketplaceOrderApplied,
  };
};

const formatFanOutIssues = (failed, skipped) => {
  const messages = [];
  if (failed > 0)
    messages.push(`${failed} ${failed === 1 ? "con error" : "con errores"}`);
  if (skipped > 0)
    messages.push(`${skipped} ${skipped === 1 ? "omitida" : "omitidas"}`);
  return messages.length > 0 ? messages.join(" · ") : formatErrorCount(0);
};

export const getFanOutDetailSummary = (movement) => {
  const outcome = getFanOutOutcome(movement);
  if (!outcome) return "";

  const messages = [];
  if (outcome.marketplaceOrderApplied > 0) {
    messages.push(
      `${pluralizePublications(outcome.marketplaceOrderApplied)} actualizada${
        outcome.marketplaceOrderApplied === 1 ? "" : "s"
      } por la orden en el marketplace`,
    );
  }
  if (outcome.updated > 0) {
    messages.push(
      `${pluralizePublications(outcome.updated)} actualizada${
        outcome.updated === 1 ? "" : "s"
      } en otros canales`,
    );
  }
  if (outcome.internalOnly > 0) {
    messages.push(
      `${pluralizePublications(outcome.internalOnly)} actualizada${
        outcome.internalOnly === 1 ? "" : "s"
      } internamente`,
    );
  }
  const issues = formatFanOutIssues(outcome.failed, outcome.skipped);
  if (issues !== formatErrorCount(0)) messages.push(issues);
  return messages.join(" · ");
};

export const getMovementResult = (movement) => {
  if (movement.movement_type === "SYNC_ADJUSTMENT") {
    const fanOutOutcome = getFanOutOutcome(movement);
    if (!fanOutOutcome) {
      return {
        primary: "Actualización realizada",
        secondary: "",
        tone: "success",
        icon: "check circle",
      };
    }
    const { completedCount, total, failed, skipped } = fanOutOutcome;
    const hasIncompleteTargets = skipped > 0;
    return {
      primary:
        total > 0
          ? `${pluralizePublications(completedCount)} actualizada${
              completedCount === 1 ? "" : "s"
            }`
          : "Sin publicaciones para actualizar",
      secondary: getFanOutDetailSummary(movement),
      tone: failed > 0 ? "error" : hasIncompleteTargets ? "warning" : "success",
      icon: failed > 0 || hasIncompleteTargets ? "warning sign" : "check circle",
    };
  }
  if (movement.movement_type === "SALE") {
    const wasApplied = Number(movement.quantity_delta || 0) < 0;
    if (wasApplied) {
      return {
        primary: "Venta procesada",
        secondary: "",
        tone: "success",
        icon: "check circle",
      };
    }

    const reason = String(movement.metadata_reason || "");
    const explicitError = SALE_ERROR_REASON_LABELS[reason];
    if (explicitError) {
      return {
        primary: "No se pudo aplicar la venta",
        secondary: explicitError,
        tone: "error",
        icon: "warning sign",
      };
    }

    const stockBefore = Number(movement.stock_before || 0);
    const stockAfter = Number(movement.stock_after || 0);
    const noChangeReason = SALE_NO_CHANGE_REASON_LABELS[reason];
    if (noChangeReason) {
      return {
        primary: "Sin cambio de stock",
        secondary: noChangeReason,
        tone: "neutral",
        icon: "info circle",
      };
    }

    if (stockBefore === 0 && stockAfter === 0) {
      return {
        primary: "Sin cambio de stock",
        secondary: "Stock ya estaba en 0",
        tone: "neutral",
        icon: "info circle",
      };
    }

    return {
      primary: "Sin cambio de stock",
      secondary: "No hubo modificación de stock",
      tone: "neutral",
      icon: "info circle",
    };
  }
  if (movement.movement_type === "ORDER_CANCEL") {
    return {
      primary: "Stock restaurado",
      secondary: "",
      tone: "success",
      icon: "check circle",
    };
  }
  return {
    primary: "Realizado",
    secondary: "",
    tone: "success",
    icon: "check circle",
  };
};

const isKitComponentSale = (row) =>
  row?.movement_type === "SALE" &&
  row?.stock_composition === "kit_component" &&
  Boolean(row?.kit_item_id);

const kitSaleGroupKey = (row) =>
  [
    row.kit_item_id,
    row.kit_listing_id || "",
    row.external_order_id || "",
    row.source_channel || "",
    row.actor_id || "",
  ].join("\u001f");

const createKitSaleSummary = (componentRows, key) => {
  const first = componentRows[0];
  return {
    id: `kit-sale:${key}`,
    presentation_type: "KIT_SALE",
    component_count: componentRows.length,
    sku: first.kit_sku || "SKU no informado",
    item_id: first.kit_item_id,
    item_title: first.kit_title || "Kit sin título",
    movement_type: "SALE",
    source_type: first.source_type,
    source_channel: first.source_channel,
    external_order_id: first.external_order_id,
    actor_type: first.actor_type,
    actor_id: first.actor_id,
    actor_label: first.actor_label,
    created_at: first.created_at,
    quantity_delta: -1,
    stock_before: 0,
    stock_after: 0,
  };
};

export const arrangeMovementRows = (rows) => {
  const rowsById = new Map(rows.map((row) => [row.id, row]));
  const childrenByParentId = new Map();
  const kitComponentGroups = new Map();
  const kitGroupByComponentId = new Map();

  rows.forEach((row) => {
    const parentId = row.parent_sale_movement_id;
    if (parentId && rowsById.has(parentId)) {
      childrenByParentId.set(parentId, [
        ...(childrenByParentId.get(parentId) || []),
        row,
      ]);
    }

    if (isKitComponentSale(row)) {
      const key = kitSaleGroupKey(row);
      const group = kitComponentGroups.get(key) || [];
      group.push(row);
      kitComponentGroups.set(key, group);
      kitGroupByComponentId.set(row.id, key);
    }
  });

  const emitted = new Set();
  const arranged = [];

  const emitKitSaleGroup = (key) => {
    const componentRows = kitComponentGroups.get(key) || [];
    if (!componentRows.length || componentRows.every((row) => emitted.has(row.id))) {
      return;
    }
    arranged.push({
      movement: createKitSaleSummary(componentRows, key),
      isChild: false,
    });
    componentRows.forEach((component) => {
      if (emitted.has(component.id)) return;
      arranged.push({
        movement: component,
        isChild: true,
        childKind: "kit-component",
      });
      emitted.add(component.id);
      (childrenByParentId.get(component.id) || []).forEach((child) => {
        if (!emitted.has(child.id)) {
          arranged.push({ movement: child, isChild: true });
          emitted.add(child.id);
        }
      });
    });
  };

  rows.forEach((row) => {
    if (emitted.has(row.id)) return;
    const componentGroupKey = kitGroupByComponentId.get(row.id);
    if (componentGroupKey) {
      emitKitSaleGroup(componentGroupKey);
      return;
    }

    const parentId = row.parent_sale_movement_id;
    const parentKitGroupKey = parentId
      ? kitGroupByComponentId.get(parentId)
      : null;
    if (parentKitGroupKey) {
      emitKitSaleGroup(parentKitGroupKey);
      return;
    }
    if (parentId && rowsById.has(parentId)) {
      const parent = rowsById.get(parentId);
      if (!emitted.has(parent.id)) {
        arranged.push({ movement: parent, isChild: false });
        emitted.add(parent.id);
      }
      (childrenByParentId.get(parent.id) || []).forEach((child) => {
        if (!emitted.has(child.id)) {
          arranged.push({ movement: child, isChild: true });
          emitted.add(child.id);
        }
      });
      return;
    }

    arranged.push({ movement: row, isChild: false });
    emitted.add(row.id);
    (childrenByParentId.get(row.id) || []).forEach((child) => {
      if (!emitted.has(child.id)) {
        arranged.push({ movement: child, isChild: true });
        emitted.add(child.id);
      }
    });
  });

  return arranged;
};
